import { FilesUtils } from './files.utils';

export const VideoUtils = {
  /**
   * Get video metadata and generate thumbnail
   * Extracts width, height, duration and generates a JPEG thumbnail from the video
   * @param videoFile - The video file to process
   * @param options - Options for thumbnail generation
   * @returns Promise resolving to video metadata and thumbnail file
   */
  getVideoInfo: (
    videoFile: File,
    options: {
      frameTimeInSeconds?: number;
    } = {}
  ): Promise<{
    width: number;
    height: number;
    duration: number;
    thumbnailFile: File;
  }> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const video = document.createElement('video');
      const context = canvas.getContext('2d');

      const urlRef = URL.createObjectURL(videoFile);
      const frameTime = options.frameTimeInSeconds ?? 0.1;
      const fileType = 'image/jpeg';
      const fileName = `${FilesUtils.getName(videoFile.name)}_thumb.jpg`;

      video.style.display = 'none';
      canvas.style.display = 'none';
      video.preload = 'metadata';

      document.body.appendChild(canvas);
      document.body.appendChild(video);

      if (!context) {
        reject(new Error(`Couldn't retrieve context 2d`));
        return;
      }

      video.onloadedmetadata = () => {
        // Set canvas dimensions
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Store metadata
        const metadata = {
          width: video.videoWidth,
          height: video.videoHeight,
          duration: video.duration,
        };

        // Generate thumbnail after video seeks to the specified frame
        video.onseeked = () => {
          context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error(`Couldn't convert canvas to blob`));
              return;
            }

            const thumbnailFile = new File([blob], fileName, { type: fileType });

            resolve({
              ...metadata,
              thumbnailFile,
            });

            // Cleanup
            video.remove();
            canvas.remove();
            URL.revokeObjectURL(urlRef);
          }, fileType);
        };

        video.onerror = () => {
          URL.revokeObjectURL(urlRef);
          video.remove();
          canvas.remove();
          reject(new Error('Failed to load video data'));
        };

        // Set the frame to capture - this will trigger 'seeked' event when ready
        video.currentTime = frameTime;
      };

      video.onerror = () => {
        URL.revokeObjectURL(urlRef);
        video.remove();
        canvas.remove();
        reject(new Error('Failed to load video metadata'));
      };

      video.src = urlRef;
    });
  },
};
